import { Formik, Field } from "formik";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import { Modal, Form, ModalBody, Button } from "react-bootstrap";
import { addTreeImage } from "../../../services/treeCrud";
import ImageDropzone from "@components/ImageDropzone";
import { ImageAddIcon } from "@components/icons";

export default function TreeImageForm({ show, setOpenForm, formValue, treeId }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const query = new URLSearchParams(useLocation().search);
  const tid = treeId || formValue?.id || formValue?.tree_id || query.get("selectedTree");

  const { isLoading, mutateAsync } = useMutation("addTreeeImage", addTreeImage, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["treeimages", tid]);

      // Update project cache to reflect any tree data changes in tree_set
      // Note: Tree images are typically fetched separately, but if tree object includes image metadata, update it
      const projectQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === "project",
      });

      projectQueries.forEach(([queryKey, projectData]) => {
        if (projectData) {
          queryClient.setQueryData(queryKey, (oldProject) => {
            if (!oldProject) return oldProject;

            return {
              ...oldProject,
              areas:
                oldProject.areas?.map((area) => ({
                  ...area,
                  tree_set:
                    area.tree_set?.map((tree) => {
                      if (tree.id === parseInt(tid)) {
                        console.log("Updating tree", tree.id, "image metadata in project cache");
                        // If tree object contains image metadata, update it here
                        // For now, just trigger a refresh by updating the tree object
                        return {
                          ...tree,
                          // Add any image metadata that might be returned from the API
                          last_image_update: new Date().toISOString(),
                        };
                      }
                      return tree;
                    }) || [],
                })) || [],
            };
          });
        }
      });

      // Update trees query cache for the trees list
      const treesQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === "trees",
      });
      treesQueries.forEach(([queryKey, treesData]) => {
        if (treesData) {
          queryClient.setQueryData(queryKey, (oldTrees) => {
            if (!oldTrees) return oldTrees;

            // Handle both paginated and non-paginated responses
            if (oldTrees.results) {
              // Paginated response
              return {
                ...oldTrees,
                results: oldTrees.results.map((tree) => {
                  if (tree.id === parseInt(tid)) {
                    console.log("Updating tree", tree.id, "image metadata in trees cache");
                    return {
                      ...tree,
                      // Add any image metadata that might be returned from the API
                      last_image_update: new Date().toISOString(),
                    };
                  }
                  return tree;
                }),
              };
            } else {
              // Non-paginated response
              return oldTrees.map((tree) => {
                if (tree.id === parseInt(tid)) {
                  console.log("Updating tree", tree.id, "image metadata in trees cache");
                  return {
                    ...tree,
                    // Add any image metadata that might be returned from the API
                    last_image_update: new Date().toISOString(),
                  };
                }
                return tree;
              });
            }
          });
        }
      });

      // Still invalidate the specific tree query for other components
      const treeIdStr = tid.toString();
      const treeIdNum = parseInt(tid);
      queryClient.removeQueries(["tree", treeIdStr]);
      queryClient.removeQueries(["tree", treeIdNum]);
      queryClient.invalidateQueries("trees");

      setOpenForm(false);
    },
  });

  const saveImage = async (values) => {
    if (!tid) {
      console.warn("TreeImageForm: missing tree id");
      return;
    }
    // eslint-isable-next-line no-undef
    const form = new FormData();
    form.append("tree_image", values.tree_image);
    form.append("leaf_image", values.leaf_image);
    form.append("timestamp", values.timestamp);
    await mutateAsync({ form, tid });
  };

  const handleClose = () => {
    setOpenForm(false);
  };
  const now = new Date();

  return (
    <>
      <Modal onHide={handleClose} show={show} centered>
        <ModalHeader closeButton>
          <h5 className="mb-0">{`${t("add_tree_img")}`}</h5>
        </ModalHeader>
        <ModalBody>
          <Formik
            initialValues={{
              tree_image: "",
              leaf_image: "",
              timestamp: now.toISOString().slice(0, 16),
            }}
            onSubmit={async (values) => saveImage(values)}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <label className="mb-2" htmlFor="">
                  {t("tree_form.info.represent.help")}
                </label>
                <label className="mb-2" htmlFor="">
                  {t("datetime_photo")}
                </label>

                <Field className="form-control" type="datetime-local" name="timestamp" />
                <label className="mb-2" htmlFor="">
                  {t("upload_tree_img")}
                </label>
                <ImageDropzone
                  img={<ImageAddIcon />}
                  setFieldValue={setFieldValue}
                  name={"tree_image"}
                />
                <label className="mb-2" htmlFor="">
                  {t("upload_leaf_img")}
                </label>
                <ImageDropzone
                  img={<ImageAddIcon />}
                  setFieldValue={setFieldValue}
                  name={"leaf_image"}
                />
                <div className="d-flex justify-content-between">
                  <Button type="submit" className="  mt-4" variant=" btn-outline-primary">
                    {!isLoading ? `${t("save")}` : "Saving..."}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>
    </>
  );
}
